import { computed, type ComputedRef, type Reactive } from 'vue';
import type { QueryArea } from 'src/models';
import type { TopMovieSearch } from 'src/models/top';
import { DEFAULT_DATE_RANGE_DAYS, MOVIE_SEARCH_RESULT_MODES, MSEC_PER_DAY } from 'src/consts/top';

const MOVIE_SEARCH_DATE_ERROR_MSG = '検索期間は一ヶ月以内で指定してください';

export interface MovieSearchParamsContext {
  isValidMovieSearchDateParams: ComputedRef<boolean>;
  isValidRouteMovieSearchDateParams: ComputedRef<boolean>;
  isValidMovieSearchParams: ComputedRef<boolean>;
  searchParamMovieSearchButtonTooltip: ComputedRef<string>;
  onDateParamChange: (prop: 'from' | 'to', val: Date) => void;
  onTagParamChange: () => void;
  onRoadNameDispChange: () => void;
  openTagModal: () => void;
}

// 検索パラメータの保持・バリデーション・日付範囲調整を担うcomposable。
// 範囲検索の再検索実行はexecution側の責務のため、doAreaMovieSearchをコールバックとして注入してもらう
// （execution側からparams側への参照は生成順で解決するため、ここではexecutionをimportしない）。
export function useMovieSearchParams(deps: {
  movieSearchState: Reactive<TopMovieSearch>;
  doAreaMovieSearch: (queryArea: QueryArea) => Promise<void>;
}): MovieSearchParamsContext {
  const { movieSearchState, doAreaMovieSearch } = deps;

  const getMovieSearchMaxDateRange = () => {
    // そのうち、過去に遡るほど検索可能範囲が広がるようにしたい.
    return 31;
  };

  const isValidRouteMovieSearchDateParams = computed(() => {
    const params = movieSearchState.params;
    return !!params.dtTo;
  });
  const isValidMovieSearchDateParams = computed(() => {
    const params = movieSearchState.params;
    const maxDateRange = getMovieSearchMaxDateRange();
    return !!((params.dtFrom && params.dtTo) &&
      (Math.abs(params.dtTo.getTime() - params.dtFrom.getTime()) < (maxDateRange + 1) * MSEC_PER_DAY));
  });
  const isValidMovieSearchParams = computed(() => {
    const isDateParamOk = isValidMovieSearchDateParams.value;
    const params = movieSearchState.params;
    const isRoadParamOk =
      (params.roadNameDispObj && params.direction) &&
      (params.kpCalcFrom === '' || /^\d+(\.\d+)?$/.test(params.kpCalcFrom || '')) &&
      (params.kpCalcTo === '' || /^\d+(\.\d+)?$/.test(params.kpCalcTo || ''));
    const isCarParamOk = params.carName;
    const isTagParamOk = params.tags && params.tags.length > 0;
    return !!(isDateParamOk && (isRoadParamOk || isCarParamOk || isTagParamOk));
  });
  const searchParamMovieSearchButtonTooltip = computed(() => {
    return isValidMovieSearchDateParams.value ? '' : MOVIE_SEARCH_DATE_ERROR_MSG;
  });

  const isAreaResultMode = () => movieSearchState.resultMode === MOVIE_SEARCH_RESULT_MODES.AREA;

  const adjustMovieSearchDateRange = (prop: 'from' | 'to') => {
    const dtProp: 'dtFrom' | 'dtTo' = prop === 'from' ? 'dtFrom' : 'dtTo';
    if (!movieSearchState.params[dtProp]) return;

    const params = movieSearchState.params;
    let otherProp: 'dtTo' | 'dtFrom';
    let sign;
    if (prop === 'from') {
      otherProp = 'dtTo';
      sign = 1;
    } else {
      otherProp = 'dtFrom';
      sign = -1;
    }

    let dateRange: number | null = null;
    if (!params[otherProp] || params.dtFrom > params.dtTo) {
      // 編集していない方の日付が正しい日付ではない または 開始日 > 終了日の場合、
      // 検索期間をデフォルト検索期間に戻す.
      dateRange = DEFAULT_DATE_RANGE_DAYS;
    } else {
      const maxDateRange = getMovieSearchMaxDateRange();
      if (params.dtTo.getTime() - params.dtFrom.getTime() > maxDateRange * MSEC_PER_DAY) {
        // 開始日 <= 終了日の場合で、検索可能期間を超えている場合、
        // 検索期間を最大検索可能期間に戻す.
        dateRange = maxDateRange;
      }
    }

    if (dateRange) {
      // 検索期間に合うよう、編集していない方の日付を調整する.
      if (prop === 'from') {
        params.dtTo = new Date(params.dtFrom.valueOf() + sign * dateRange * MSEC_PER_DAY);
      } else {
        params.dtFrom = new Date(params.dtTo.valueOf() + sign * dateRange * MSEC_PER_DAY);
      }
    }
  };
  const onDateParamChange = (prop: 'from' | 'to', val: Date) => {
    const dtProp: 'dtFrom' | 'dtTo' = prop === 'from' ? 'dtFrom' : 'dtTo';
    movieSearchState.params[dtProp] = val;

    adjustMovieSearchDateRange(prop);

    // 範囲検索の場合、再検索
    if (
      isAreaResultMode() &&
      isValidMovieSearchDateParams.value &&
      movieSearchState.areaQueryObj
    ) {
      doAreaMovieSearch(movieSearchState.areaQueryObj);
    }
  };
  const onTagParamChange = () => {
    movieSearchState.showTagModal = false;
    // 範囲検索の場合、再検索
    if (isAreaResultMode() && movieSearchState.areaQueryObj) {
      doAreaMovieSearch(movieSearchState.areaQueryObj);
    }
  };
  const onRoadNameDispChange = () => {
    movieSearchState.params.direction = '';
    movieSearchState.params.kpCalcFrom = '';
    movieSearchState.params.kpCalcTo = '';
  };
  const openTagModal = () => { movieSearchState.showTagModal = true; };

  return {
    isValidMovieSearchDateParams,
    isValidRouteMovieSearchDateParams,
    isValidMovieSearchParams,
    searchParamMovieSearchButtonTooltip,
    onDateParamChange,
    onTagParamChange,
    onRoadNameDispChange,
    openTagModal,
  };
}
